import { useEffect, useRef, useState, type ComponentType } from "react";
import { ApiError, api, type ChatMessageView } from "../api";
import { ASSISTANT_NAME } from "../brand";
import { IconCoin, IconFamily, IconHeart, IconMoonPath, IconPause, IconPlay, IconSend } from "../icons";

interface Props {
  onPaywall: () => void;
}

const CHIPS: { icon: ComponentType; label: string; starter: string }[] = [
  { icon: IconHeart, label: "Отношения", starter: "В отношениях: " },
  { icon: IconCoin, label: "Деньги и работа", starter: "С деньгами и работой: " },
  { icon: IconMoonPath, label: "Я и мой путь", starter: "Про себя и свой путь: " },
  { icon: IconFamily, label: "Семья и близкие", starter: "В семье: " },
];

const ERROR_TEXT: Record<string, string> = {
  tier_required: "Бесплатный вопрос уже задан. На Полном тарифе — 5 вопросов в день, и я буду помнить весь наш разговор.",
  quota_exceeded: "На сегодня вопросы закончились — новый лимит откроется завтра.",
  ai_disabled: "ИИ-консультант сейчас временно выключен.",
  ai_unavailable: "Не получилось ответить — попробуй ещё раз через минуту.",
  empty_question: "Опиши вопрос текстом.",
  not_onboarded: "Сначала укажи дату рождения в профиле.",
};

function AudioBubble({ base64 }: { base64: string }) {
  const ref = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState<number | null>(null);
  const src = `data:audio/mpeg;base64,${base64}`;

  const toggle = () => {
    const el = ref.current;
    if (!el) return;
    if (playing) {
      el.pause();
    } else {
      void el.play();
    }
  };

  const fmt = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

  return (
    <div className="chat-audio">
      <button type="button" className="chat-audio-play" onClick={toggle} aria-label={playing ? "Пауза" : "Слушать"}>
        {playing ? <IconPause /> : <IconPlay />}
      </button>
      <span className="chat-audio-wave" aria-hidden>
        {Array.from({ length: 24 }, (_, i) => (
          <i key={i} style={{ height: `${20 + ((i * 37) % 60)}%` }} />
        ))}
      </span>
      <span className="chat-audio-time">{duration ? fmt(duration) : ""}</span>
      <audio
        ref={ref}
        src={src}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
      />
    </div>
  );
}

/** Вкладка «Спросить»: диалог с {ASSISTANT_NAME}, с памятью (Полный тариф) и озвучкой каждого ответа — в истории тоже. */
export function Chat({ onPaywall }: Props) {
  const [messages, setMessages] = useState<ChatMessageView[] | null>(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let alive = true;
    api.chatHistory().then(
      (r) => alive && setMessages(r.items),
      () => alive && setMessages([]),
    );
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, sending]);

  const send = async (text: string) => {
    const q = text.trim();
    if (!q || sending) return;
    setSending(true);
    setErrorCode(null);
    setInput("");
    setMessages((m) => [...(m ?? []), { role: "user", text: q, audioBase64: null, createdAt: new Date().toISOString() }]);
    try {
      const r = await api.askChat(q);
      setMessages((m) => [...(m ?? []), { role: "assistant", text: r.text, audioBase64: r.audioBase64, createdAt: new Date().toISOString() }]);
    } catch (e) {
      setMessages((m) => (m ?? []).slice(0, -1));
      setInput(q);
      setErrorCode(e instanceof ApiError ? e.code : "ai_unavailable");
    } finally {
      setSending(false);
    }
  };

  if (messages === null) return <div className="warm center" />;

  if (messages.length === 0) {
    return (
      <div className="warm chatintro">
        <p className="chatintro-eyebrow">СПРОСИТЬ</p>
        <h1 className="chatintro-title">Расскажи, что происходит</h1>
        <p className="chatintro-sub">Чем подробнее опишешь ситуацию, тем точнее отвечу. Твои числа я уже знаю.</p>

        <div className="chatintro-chips">
          {CHIPS.map((c) => (
            <button key={c.label} type="button" onClick={() => setInput(c.starter)}>
              <c.icon /> {c.label}
            </button>
          ))}
        </div>

        <textarea
          className="chatintro-textarea"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Например: мы вместе два года. Последний месяц он отдалился, отвечает коротко. Не понимаю — это временно или он хочет уйти. Поговорить прямо или дать время?"
          rows={5}
        />
        <div className="chatintro-hint">
          <span>Опиши ситуацию своими словами — {ASSISTANT_NAME} ответит голосом</span>
        </div>

        {errorCode && <p className="chaterror">{ERROR_TEXT[errorCode] ?? ERROR_TEXT.ai_unavailable}</p>}

        <button type="button" className="chatintro-send" disabled={!input.trim() || sending} onClick={() => void send(input)}>
          <IconSend />
          {sending ? "Спрашиваю…" : "Спросить"}
        </button>
        <p className="chatintro-footnote">Первый вопрос бесплатный — опиши ситуацию подробно.</p>
      </div>
    );
  }

  const paywall = errorCode === "tier_required" || errorCode === "quota_exceeded";

  return (
    <div className="warm chatthread">
      <div className="chatthread-scroll">
        {messages.map((m, i) => {
          const hasAudio = m.role === "assistant" && !!m.audioBase64;
          return (
            <div key={i} className={m.role === "user" ? "chatbubble chatbubble-user" : "chatbubble chatbubble-assistant"}>
              {/* Ответ — голосом; текст показываем только если озвучка недоступна (честный fallback, не прячем ответ без причины). */}
              {!hasAudio && <p>{m.text}</p>}
              {hasAudio && <AudioBubble base64={m.audioBase64 as string} />}
            </div>
          );
        })}
        {sending && (
          <div className="chattyping">
            <span className="chattyping-dot" aria-hidden />
            Готовлю ответ…
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {paywall ? (
        <div className="chatpaywall">
          <p>{ERROR_TEXT[errorCode]}</p>
          <button type="button" onClick={onPaywall}>
            Открыть Полный тариф
          </button>
        </div>
      ) : (
        <div className="chatinput">
          {errorCode && <p className="chaterror">{ERROR_TEXT[errorCode] ?? ERROR_TEXT.ai_unavailable}</p>}
          <div className="chatinput-row">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && void send(input)}
              placeholder="Сообщение"
              aria-label="Сообщение"
              disabled={sending}
            />
            <button type="button" className="chatinput-send" disabled={!input.trim() || sending} onClick={() => void send(input)} aria-label="Отправить">
              <IconSend />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
